// Bilingual dictionary for the AI Photo Detector app.
export const translations = {
  en: {
    appName: "Forensic AI Photo Detector",
    tagline: "Real or AI? Inspect any image with metadata, pixel forensics, and Gemini 2.5 Pro vision.",
    nav: {
      analyze: "Analyze",
      history: "History",
      analyzeNew: "Analyze new",
    },
    upload: {
      dropzone_title: "Drop image here or click to upload",
      dropzone_hint: "JPEG, PNG, WebP · max 15MB",
      analyze_button: "Analyze image",
      analyzing: "Analyzing…",
      reset: "Reset",
      privacy_note: "Images are removed from stored history every hour.",
      pick_file: "Pick a file",
      sample_label: "Or try a sample:",
      sample_real: "Real photo",
      sample_ai: "Likely AI",
    },
    pipeline: {
      uploading: "Uploading",
      metadata: "Metadata",
      ela: "ELA",
      vision: "AI Vision",
      done: "Done",
    },
    verdict: {
      label: "Verdict",
      REAL: "Real photograph",
      AI: "AI-generated",
      UNCERTAIN: "Uncertain",
      confidence: "Confidence",
      regions_found: "suspicious region",
      regions_found_plural: "suspicious regions",
      analyzed_in: "Analyzed in",
    },
    viewer: {
      title: "Image inspector",
      original: "Original",
      bboxes: "AI regions",
      ela: "ELA heatmap",
      opacity: "Opacity",
      legend_title: "Severity",
      severity_low: "Low",
      severity_medium: "Medium",
      severity_high: "High",
      no_regions: "No suspicious regions detected.",
      image_unavailable: "Image removed by cleanup job.",
    },
    regions: {
      title: "AI suspicious regions",
      empty: "No suspicious regions found by Gemini 2.5 Pro.",
      severity: "Severity",
    },
    tabs: {
      vision: "AI Vision",
      metadata: "Metadata",
      forensics: "Pixel Forensics",
      breakdown: "Score Breakdown",
    },
    vision: {
      heading: "Gemini 2.5 Pro Reasoning",
      key_indicators: "Key indicators",
      no_indicators: "No specific indicators reported.",
      no_reasoning: "No reasoning available.",
    },
    metadata: {
      heading: "File & EXIF metadata",
      highlights: "Suspicious highlights",
      no_highlights: "No suspicious metadata fields found.",
      search_placeholder: "Search keys / values…",
      key: "Key",
      value: "Value",
      copy: "Copy JSON",
      copied: "Metadata copied to clipboard",
      no_exif: "No EXIF data present in this file.",
      file_size: "File size",
      format: "Format",
      dimensions: "Dimensions",
      camera: "Camera",
      software: "Software",
      datetime: "Original date",
      ai_signature: "AI signature",
      c2pa: "C2PA credentials",
      c2pa_manifest: "C2PA manifest",
      c2pa_no_manifest: "C2PA markers were detected, but no manifest JSON was returned.",
      c2pa_parse_error: "Unable to parse C2PA manifest",
      signed_by: "Signed by",
      issued_date: "Issued date",
      algorithm: "Algorithm",
      cert_status: "Cert status",
      tree: "Tree",
      raw_json: "Raw JSON",
      detected: "Detected",
      not_detected: "Not detected",
      not_present: "Not present",
    },
    forensics: {
      heading: "Pixel-level forensic signals",
      ela_title: "Error Level Analysis (ELA)",
      noise_title: "Noise & frequency analysis",
      anomaly_score: "Anomaly score",
      mean_diff: "Mean diff",
      std_diff: "Std deviation",
      max_diff: "Max diff",
      quad_var: "Quadrant variance",
      laplacian: "Laplacian variance",
      noise_mean: "Noise mean",
      noise_std: "Noise std",
      hf_ratio: "High-freq ratio",
      edge_density: "Edge density",
      histogram_title: "ELA intensity histogram",
    },
    breakdown: {
      heading: "How we reached the verdict",
      ai_score: "AI score",
      real_score: "Real score",
      signal: "Signal",
      impact: "Impact",
      weight: "Weight",
      detail: "Detail",
      no_signals: "No contributing signals.",
    },
    history: {
      title: "Analysis history",
      subtitle: "Every analysis is saved here. Click any item to re-open the full report.",
      empty_title: "No analyses yet",
      empty_subtitle: "Run your first analysis to populate the history.",
      go_analyze: "Start analyzing",
      delete: "Delete",
      view: "View report",
      clear_all: "Clear all",
      clear_confirm: "Are you sure you want to clear all history?",
      cleared: "All history cleared",
      deleted: "Record deleted",
      duration: "Analyzed in",
    },
    export: {
      json: "Download JSON report",
      copy_json: "Copy JSON",
      json_copied: "Report JSON copied",
      png: "Download annotated image",
      png_saved: "Image downloaded",
    },
    errors: {
      upload_failed: "Upload failed",
      invalid_file: "Invalid image file",
      generic: "Something went wrong. Please try again.",
      file_too_large: "File is too large. Max 15MB.",
      bad_format: "Unsupported format. Use JPEG, PNG, or WebP.",
    },
    common: {
      or: "or",
      back: "Back",
      yes: "Yes",
      no: "No",
      cancel: "Cancel",
    },
  },
  id: {
    appName: "Detektor Foto AI Forensik",
    tagline: "Asli atau AI? Periksa setiap gambar dengan metadata, forensik piksel, dan analisis vision Gemini 2.5 Pro.",
    nav: {
      analyze: "Analisis",
      history: "Riwayat",
      analyzeNew: "Analisis baru",
    },
    upload: {
      dropzone_title: "Letakkan gambar di sini atau klik untuk unggah",
      dropzone_hint: "JPEG, PNG, WebP · maks 15MB",
      analyze_button: "Analisis gambar",
      analyzing: "Menganalisis…",
      reset: "Reset",
      privacy_note: "Gambar dihapus dari riwayat tersimpan setiap jam.",
      pick_file: "Pilih file",
      sample_label: "Atau coba contoh:",
      sample_real: "Foto asli",
      sample_ai: "Kemungkinan AI",
    },
    pipeline: {
      uploading: "Mengunggah",
      metadata: "Metadata",
      ela: "ELA",
      vision: "Vision AI",
      done: "Selesai",
    },
    verdict: {
      label: "Putusan",
      REAL: "Foto asli",
      AI: "Buatan AI",
      UNCERTAIN: "Tidak yakin",
      confidence: "Keyakinan",
      regions_found: "area mencurigakan",
      regions_found_plural: "area mencurigakan",
      analyzed_in: "Dianalisis dalam",
    },
    viewer: {
      title: "Pemeriksa gambar",
      original: "Asli",
      bboxes: "Area AI",
      ela: "Heatmap ELA",
      opacity: "Opasitas",
      legend_title: "Tingkat",
      severity_low: "Rendah",
      severity_medium: "Sedang",
      severity_high: "Tinggi",
      no_regions: "Tidak ada area mencurigakan terdeteksi.",
      image_unavailable: "Gambar telah dihapus oleh job pembersihan.",
    },
    regions: {
      title: "Area mencurigakan AI",
      empty: "Tidak ada area mencurigakan yang ditemukan oleh Gemini 2.5 Pro.",
      severity: "Tingkat",
    },
    tabs: {
      vision: "Vision AI",
      metadata: "Metadata",
      forensics: "Forensik Piksel",
      breakdown: "Rincian Skor",
    },
    vision: {
      heading: "Penalaran Gemini 2.5 Pro",
      key_indicators: "Indikator utama",
      no_indicators: "Tidak ada indikator spesifik dilaporkan.",
      no_reasoning: "Tidak ada penalaran tersedia.",
    },
    metadata: {
      heading: "Metadata file & EXIF",
      highlights: "Sorotan mencurigakan",
      no_highlights: "Tidak ada bidang metadata mencurigakan.",
      search_placeholder: "Cari kunci / nilai…",
      key: "Kunci",
      value: "Nilai",
      copy: "Salin JSON",
      copied: "Metadata disalin",
      no_exif: "Tidak ada data EXIF di file ini.",
      file_size: "Ukuran file",
      format: "Format",
      dimensions: "Dimensi",
      camera: "Kamera",
      software: "Software",
      datetime: "Tanggal asli",
      ai_signature: "Tanda AI",
      c2pa: "Kredensial C2PA",
      c2pa_manifest: "Manifest C2PA",
      c2pa_no_manifest: "Marker C2PA terdeteksi, tetapi JSON manifest tidak dikembalikan.",
      c2pa_parse_error: "Manifest C2PA tidak dapat diparse",
      signed_by: "Ditandatangani oleh",
      issued_date: "Tanggal diterbitkan",
      algorithm: "Algoritma",
      cert_status: "Status sertifikat",
      tree: "Tree",
      raw_json: "JSON mentah",
      detected: "Terdeteksi",
      not_detected: "Tidak terdeteksi",
      not_present: "Tidak ada",
    },
    forensics: {
      heading: "Sinyal forensik level piksel",
      ela_title: "Error Level Analysis (ELA)",
      noise_title: "Analisis noise & frekuensi",
      anomaly_score: "Skor anomali",
      mean_diff: "Selisih rata-rata",
      std_diff: "Standar deviasi",
      max_diff: "Selisih maks",
      quad_var: "Varians kuadran",
      laplacian: "Varians Laplacian",
      noise_mean: "Rata-rata noise",
      noise_std: "Std noise",
      hf_ratio: "Rasio frekuensi tinggi",
      edge_density: "Kepadatan tepi",
      histogram_title: "Histogram intensitas ELA",
    },
    breakdown: {
      heading: "Cara kami mencapai putusan",
      ai_score: "Skor AI",
      real_score: "Skor Asli",
      signal: "Sinyal",
      impact: "Dampak",
      weight: "Bobot",
      detail: "Detail",
      no_signals: "Tidak ada sinyal kontribusi.",
    },
    history: {
      title: "Riwayat analisis",
      subtitle: "Setiap analisis disimpan di sini. Klik untuk membuka kembali.",
      empty_title: "Belum ada analisis",
      empty_subtitle: "Jalankan analisis pertama Anda untuk mengisi riwayat.",
      go_analyze: "Mulai analisis",
      delete: "Hapus",
      view: "Lihat laporan",
      clear_all: "Hapus semua",
      clear_confirm: "Anda yakin ingin menghapus semua riwayat?",
      cleared: "Semua riwayat dihapus",
      deleted: "Catatan dihapus",
      duration: "Dianalisis dalam",
    },
    export: {
      json: "Unduh laporan JSON",
      copy_json: "Salin JSON",
      json_copied: "JSON laporan disalin",
      png: "Unduh gambar dengan anotasi",
      png_saved: "Gambar diunduh",
    },
    errors: {
      upload_failed: "Unggah gagal",
      invalid_file: "File gambar tidak valid",
      generic: "Terjadi kesalahan. Coba lagi.",
      file_too_large: "File terlalu besar. Maks 15MB.",
      bad_format: "Format tidak didukung. Gunakan JPEG, PNG, atau WebP.",
    },
    common: {
      or: "atau",
      back: "Kembali",
      yes: "Ya",
      no: "Tidak",
      cancel: "Batal",
    },
  },
};

export function getTranslator(lang) {
  const dict = translations[lang] || translations.en;
  return (key) => {
    const parts = key.split(".");
    let val = dict;
    for (const p of parts) {
      if (val && Object.prototype.hasOwnProperty.call(val, p)) {
        val = val[p];
      } else {
        return key;
      }
    }
    return val;
  };
}
